import { orderTypeFixture } from '@/lib/fixtures'
import { initialFormValues, type FormDefinition, type FormValues } from '@/lib/forms/form-state'
import type { Translator } from '@/lib/i18n'

/* The Order types configuration form (VIS-321's first consumer), over the synthetic fixture. */

const WHOLE_NUMBER = /^\d+$/
const AMOUNT = /^\d+(\.\d{1,2})?$/

export function orderTypeDefinition(t: Translator): FormDefinition {
  const languages = orderTypeFixture.contentLanguages
  const defaultLanguage = languages[0].value
  return {
    contentLanguages: languages,
    sections: [
      { id: 'basics', title: t('orderTypes.sections.basics'), summary: () => t('orderTypes.summaries.basics') },
      {
        id: 'limits',
        title: t('orderTypes.sections.limits'),
        // TECH-DEBT: VIS-327 — the minimum spend is shown as a fixed amount until tenant currency formatting exists.
        summary: (saved) => t('orderTypes.summaries.limits', { orders: Number(saved.maxOrders), spend: '£0' }),
      },
      { id: 'behaviour', title: t('orderTypes.sections.behaviour'), summary: () => t('orderTypes.summaries.behaviour') },
      {
        id: 'destinations',
        title: t('orderTypes.sections.destinations'),
        summary: () => t('orderTypes.summaries.destinations'),
      },
    ],
    fields: [
      {
        id: 'displayName',
        section: 'basics',
        kind: 'text',
        label: t('orderTypes.fields.displayName'),
        required: true,
        translatable: true,
        maxLength: 80,
      },
      { id: 'enabled', section: 'basics', kind: 'checkbox', label: t('orderTypes.enabled') },
      { id: 'maxOrders', section: 'limits', kind: 'number', label: t('orderTypes.fields.maxOrders'), required: true },
      {
        id: 'minimumSpend',
        section: 'limits',
        kind: 'number',
        inputMode: 'decimal',
        label: t('orderTypes.fields.minimumSpend'),
        help: t('orderTypes.help.minimumSpend'),
        required: true,
      },
      {
        id: 'reachedMessage',
        section: 'limits',
        kind: 'textarea',
        label: t('orderTypes.fields.reachedMessage'),
        translatable: true,
      },
      { id: 'paymentRequired', section: 'behaviour', kind: 'checkbox', label: t('orderTypes.fields.paymentRequired') },
      {
        id: 'depositPercent',
        section: 'behaviour',
        kind: 'number',
        label: t('orderTypes.fields.depositPercent'),
        help: t('orderTypes.help.depositPercent'),
        visible: (values) => values.paymentRequired === true,
      },
      {
        id: 'outletRouting',
        section: 'destinations',
        kind: 'select',
        label: t('orderTypes.fields.outletRouting'),
        options: [
          { value: 'default', label: t('orderTypes.fields.defaultOutlet') },
          { value: 'dynamic', label: t('orderTypes.fields.dynamicOutlet') },
        ],
      },
      {
        id: 'fallbackOutlet',
        section: 'destinations',
        kind: 'select',
        label: t('orderTypes.fields.fallbackOutlet'),
        help: t('orderTypes.routingRevealed'),
        options: orderTypeFixture.options.outlets,
        visible: (values) => values.outletRouting === 'dynamic',
      },
    ],
    validate: (values) => {
      const errors: Record<string, string> = {}
      if (!String(values[`displayName:${defaultLanguage}`]).trim()) {
        errors[`displayName:${defaultLanguage}`] = 'orderTypes.errors.displayName'
      }
      const maxOrders = String(values.maxOrders).trim()
      if (!WHOLE_NUMBER.test(maxOrders) || Number(maxOrders) < 1) errors.maxOrders = 'orderTypes.errors.maxOrders'
      if (!AMOUNT.test(String(values.minimumSpend).trim())) errors.minimumSpend = 'orderTypes.errors.minimumSpend'
      const deposit = String(values.depositPercent).trim()
      if (values.paymentRequired === true && (!WHOLE_NUMBER.test(deposit) || Number(deposit) > 100)) {
        errors.depositPercent = 'orderTypes.errors.depositPercent'
      }
      return errors
    },
  }
}

export function orderTypeInitialValues(definition: FormDefinition): FormValues {
  const { values, translations } = orderTypeFixture
  return initialFormValues(definition, (field, language) => {
    const saved = (values as Record<string, string | number | boolean>)[field.id]
    if (language && language !== definition.contentLanguages?.[0]?.value) {
      return translations[language]?.[field.id as 'displayName' | 'reachedMessage'] ?? ''
    }
    return typeof saved === 'number' ? String(saved) : saved
  })
}
